import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { RecurrencePatternInput } from 'types/crm';

/**
 * UI-state ⇄ RecurrencePatternInput mapping for the CRM Task recurrence picker
 * (POGO-13345). Unlike the calendar's `recurrence.ts` (which expands a rule
 * into concrete client-side dates), this module maps the dialog's local state
 * to the platform-wide `RecurrencePatternDTO` shape the backend persists — the
 * pure, unit-testable seam between the picker controls and the mutation input.
 *
 * The pattern is anchored on the task's due date: the backend's
 * `validateRecurrenceInput` asserts a monthly `dayOfMonth`/`weekOfMonth`+
 * `dayOfWeek` and an annual `dayOfMonth`/`monthOfYear` line up with the anchor,
 * evaluated in UTC (`moment(startDate).tz('UTC')`). So every anchor-derived
 * field here is read from the UTC representation of the due instant to match.
 */

dayjs.extend(utc);

/** The picker's frequency choices — each maps to a `frequency`+`interval` pair. */
export type RecurrenceUiFrequency = 'none' | 'daily' | 'weekly' | 'fortnightly' | 'monthlyDate' | 'monthlyNth' | 'annually';

export type RecurrenceEndMode = 'never' | 'count' | 'date';

export type RecurrenceUiState = {
    frequency: RecurrenceUiFrequency;
    /** "every N" units — ignored for fortnightly (always 2 weeks) and none. */
    interval: number;
    /** 0=Sun..6=Sat, for weekly / fortnightly. */
    daysOfWeek: number[];
    endMode: RecurrenceEndMode;
    endAfterOccurrence: number;
    /** ISO date (or null) when endMode is 'date'. */
    endDate: string | null;
};

/** Mon-first weekday chips; values are the DTO's 0=Sun..6=Sat. */
export const WEEKDAYS: { day: number; label: string }[] = [
    { day: 1, label: 'Mon' },
    { day: 2, label: 'Tue' },
    { day: 3, label: 'Wed' },
    { day: 4, label: 'Thu' },
    { day: 5, label: 'Fri' },
    { day: 6, label: 'Sat' },
    { day: 0, label: 'Sun' }
];

export const FREQUENCY_OPTIONS: { value: RecurrenceUiFrequency; label: string }[] = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly (choose days)' },
    { value: 'fortnightly', label: 'Fortnightly (choose days)' },
    { value: 'monthlyDate', label: 'Monthly (on the date)' },
    { value: 'monthlyNth', label: 'Monthly (Nth weekday)' },
    { value: 'annually', label: 'Annually' }
];

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'];
const WEEKDAY_LABEL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_LABEL = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

/** The default (off) picker state — a plain, non-recurring task. */
export const defaultRecurrenceUiState = (): RecurrenceUiState => ({
    frequency: 'none',
    interval: 1,
    daysOfWeek: [],
    endMode: 'never',
    endAfterOccurrence: 5,
    endDate: null
});

export const isRecurringUi = (state: RecurrenceUiState): boolean => state.frequency !== 'none';

/** Which UTC weekday-occurrence of its month the anchor falls on: 0-indexed. */
const weekOfMonthUtc = (anchor: dayjs.Dayjs): number => Math.ceil(anchor.date() / 7) - 1;

/**
 * Map the picker state to a `RecurrencePatternInput`, anchored on the task's
 * due date (`dueIso`). Returns null when the task doesn't repeat or has no due
 * date to anchor on. Anchor-derived fields are taken in UTC to satisfy the
 * backend validator.
 */
export const toRecurrencePatternInput = (state: RecurrenceUiState, dueIso?: string | null): RecurrencePatternInput | null => {
    if (state.frequency === 'none' || !dueIso) {
        return null;
    }
    const anchor = dayjs.utc(dueIso);

    const endBits: Pick<RecurrencePatternInput, 'endType' | 'endAfterOccurrence' | 'endDate'> = (() => {
        if (state.endMode === 'count') {
            return { endType: 'occurrence', endAfterOccurrence: state.endAfterOccurrence };
        }
        if (state.endMode === 'date' && state.endDate) {
            return { endType: 'date', endDate: state.endDate };
        }
        return { endType: 'never' };
    })();

    const interval = Math.max(1, Math.floor(state.interval || 1));

    switch (state.frequency) {
        case 'daily':
            return { frequency: 'day', interval, ...endBits };
        case 'weekly':
            return { frequency: 'week', interval, daysOfWeek: normaliseDays(state.daysOfWeek, anchor), ...endBits };
        case 'fortnightly':
            return { frequency: 'week', interval: 2, daysOfWeek: normaliseDays(state.daysOfWeek, anchor), ...endBits };
        case 'monthlyDate':
            return { frequency: 'month', interval, dayOfMonth: anchor.date(), ...endBits };
        case 'monthlyNth':
            return { frequency: 'month', interval, weekOfMonth: weekOfMonthUtc(anchor), dayOfWeek: anchor.day(), ...endBits };
        case 'annually':
            return { frequency: 'year', interval, dayOfMonth: anchor.date(), monthOfYear: anchor.month(), ...endBits };
        default:
            return null;
    }
};

/** Fall back to the anchor's own weekday when no days are chosen. */
const normaliseDays = (days: number[], anchor: dayjs.Dayjs): number[] => (days.length ? [...days].sort((a, b) => a - b) : [anchor.day()]);

/**
 * Inverse mapping — rebuild the picker state from a persisted pattern so
 * reopening the dialog shows the rule back.
 */
export const fromRecurrencePattern = (pattern?: RecurrencePatternInput | null): RecurrenceUiState => {
    const base = defaultRecurrenceUiState();
    if (!pattern || !pattern.frequency) {
        return base;
    }

    const endMode: RecurrenceEndMode = pattern.endType === 'occurrence' ? 'count' : pattern.endType === 'date' ? 'date' : 'never';
    const end = {
        endMode,
        endAfterOccurrence: pattern.endAfterOccurrence ?? base.endAfterOccurrence,
        endDate: pattern.endDate ?? null
    };
    const interval = pattern.interval ?? 1;

    switch (pattern.frequency) {
        case 'day':
            return { ...base, frequency: 'daily', interval, ...end };
        case 'week':
            return {
                ...base,
                frequency: interval === 2 ? 'fortnightly' : 'weekly',
                interval: interval === 2 ? 1 : interval,
                daysOfWeek: pattern.daysOfWeek ?? [],
                ...end
            };
        case 'month':
            return {
                ...base,
                frequency: pattern.weekOfMonth != null ? 'monthlyNth' : 'monthlyDate',
                interval,
                ...end
            };
        case 'year':
            return { ...base, frequency: 'annually', interval, ...end };
        default:
            return base;
    }
};

const ordinal = (weekOfMonth: number): string => (weekOfMonth >= 0 && weekOfMonth < ORDINALS.length ? ORDINALS[weekOfMonth] : 'last');

const dayNames = (days: number[]): string =>
    WEEKDAYS.filter((w) => days.includes(w.day))
        .map((w) => w.label)
        .join(', ');

/**
 * A plain-language summary of the rule for the live info Alert — mirrors the
 * calendar picker's summary, adapted to the "complete → mint next" model
 * (one open occurrence at a time, not N pre-created rows).
 */
export const describeRecurrence = (state: RecurrenceUiState, dueIso?: string | null): string => {
    if (state.frequency === 'none') {
        return '';
    }
    if (!dueIso) {
        return 'Pick a due date to anchor the recurrence.';
    }
    const anchor = dayjs.utc(dueIso);
    const every = (n: number, unit: string) => (n > 1 ? `every ${n} ${unit}s` : `every ${unit}`);

    let lead: string;
    switch (state.frequency) {
        case 'daily':
            lead = `Repeats ${every(state.interval, 'day')}`;
            break;
        case 'weekly': {
            const days = dayNames(state.daysOfWeek.length ? state.daysOfWeek : [anchor.day()]);
            lead = `Repeats ${every(state.interval, 'week')} on ${days}`;
            break;
        }
        case 'fortnightly': {
            const days = dayNames(state.daysOfWeek.length ? state.daysOfWeek : [anchor.day()]);
            lead = `Repeats every 2 weeks on ${days}`;
            break;
        }
        case 'monthlyDate':
            lead = `Repeats ${every(state.interval, 'month')} on day ${anchor.date()}`;
            break;
        case 'monthlyNth':
            lead = `Repeats ${every(state.interval, 'month')} on the ${ordinal(weekOfMonthUtc(anchor))} ${WEEKDAY_LABEL[anchor.day()]}`;
            break;
        case 'annually':
            lead = `Repeats ${every(state.interval, 'year')} on ${anchor.date()} ${MONTH_LABEL[anchor.month()]}`;
            break;
        default:
            lead = 'Repeats';
    }

    let ends = '';
    if (state.endMode === 'count') {
        ends = `, ${state.endAfterOccurrence} time${state.endAfterOccurrence === 1 ? '' : 's'}`;
    } else if (state.endMode === 'date' && state.endDate) {
        ends = `, until ${dayjs(state.endDate).format('D MMM YYYY')}`;
    }

    return `${lead}${ends}. Completing this task creates the next occurrence automatically.`;
};
